// Aggregator API layer for email templates (US-001 / US-002).
// Mirrors Conditions/infrastructure/conditionAgr.api.ts. Endpoints: see
// plans/email-template-agr/backend-api-contract.md (aggregator API).
import request from "../../../request";
// DTO types live in the application layer (mirrors Conditions/CancellationPolicy) so the
// application helpers don't import from infrastructure; infrastructure depends on application.
import type { EmailTemplateDTO, AgrTemplateMap } from "../application/emailTemplateForm.helpers";

export type { EmailTemplateDTO, AgrTemplateMap };

export interface AssignedEnginesRequest {
  type: string;
  destination: string;
  excludeId?: number;
}

export interface ExcelResult {
  data: Blob;
  filename: string;
}

const AGR_BASE = "/api/emails/custom/aggregator";
const INDIVIDUAL_BASE = "/api/emails/custom";

function createClient(baseURL: string) {
  return request.create({ baseURL });
}

// GET /api/emails/custom/aggregator/{propertyTicker}/templates/{id}
export async function getAgrTemplates(
  baseURL: string,
  propertyTicker: string,
  id: number,
  signal?: AbortSignal
): Promise<AgrTemplateMap> {
  const httpClient = createClient(baseURL);
  const response = await httpClient.get(`${AGR_BASE}/${propertyTicker}/templates/${id}`, {
    headers: { "x-property": propertyTicker },
    signal,
  });
  return response.data || {};
}

// POST /api/emails/custom/aggregator/{propertyTicker}/templates?forcePreferred=
export async function createAgrTemplates(
  baseURL: string,
  propertyTicker: string,
  map: AgrTemplateMap,
  forcePreferred: boolean
): Promise<void> {
  const httpClient = createClient(baseURL);
  await httpClient.post(`${AGR_BASE}/${propertyTicker}/templates`, map, {
    headers: { "x-property": propertyTicker },
    params: { forcePreferred },
  });
}

// PUT /api/emails/custom/aggregator/{propertyTicker}/templates/{id}?forcePreferred=
export async function updateAgrTemplates(
  baseURL: string,
  propertyTicker: string,
  id: number,
  map: AgrTemplateMap,
  forcePreferred: boolean
): Promise<void> {
  const httpClient = createClient(baseURL);
  await httpClient.put(`${AGR_BASE}/${propertyTicker}/templates/${id}`, map, {
    headers: { "x-property": propertyTicker },
    params: { forcePreferred },
  });
}

// DELETE /api/emails/custom/aggregator/{propertyTicker}/templates/{id}
export async function deleteAgrTemplates(
  baseURL: string,
  propertyTicker: string,
  id: number
): Promise<void> {
  const httpClient = createClient(baseURL);
  await httpClient.delete(`${AGR_BASE}/${propertyTicker}/templates/${id}`, {
    headers: { "x-property": propertyTicker },
  });
}

// POST /api/emails/custom/aggregator/{propertyTicker}/templates/assigned-engines
export async function fetchAssignedEngines(
  baseURL: string,
  propertyTicker: string,
  requestBody: AssignedEnginesRequest,
  signal?: AbortSignal
): Promise<string[]> {
  const httpClient = createClient(baseURL);
  const response = await httpClient.post(
    `${AGR_BASE}/${propertyTicker}/templates/assigned-engines`,
    requestBody,
    { headers: { "x-property": propertyTicker }, signal }
  );
  return response.data || [];
}

// GET /api/emails/custom/{propertyTicker}/templates — the engine's own templates plus the copies
// inherited from its aggregator.
export async function getPropertyTemplates(
  baseURL: string,
  propertyTicker: string,
  signal?: AbortSignal
): Promise<EmailTemplateDTO[]> {
  const httpClient = createClient(baseURL);
  const response = await httpClient.get(`${INDIVIDUAL_BASE}/${propertyTicker}/templates`, {
    headers: { "x-property": propertyTicker },
    signal,
  });
  return response.data || [];
}

// GET /api/emails/custom/{propertyTicker}/templates/xlsx  (individual base, blob)
export async function downloadTemplatesExcel(
  baseURL: string,
  propertyTicker: string
): Promise<ExcelResult> {
  const httpClient = createClient(baseURL);
  const response = await httpClient.get(`${INDIVIDUAL_BASE}/${propertyTicker}/templates/xlsx`, {
    headers: { "x-property": propertyTicker },
    responseType: "blob",
  });
  const contentDisposition: string = response.headers["content-disposition"] || "";
  const filenamePart = contentDisposition.split(";")[1];
  const filename = filenamePart
    ? filenamePart.trim().split("=")[1].replace(/"/g, "")
    : `email-templates-${propertyTicker}-${Date.now()}.xlsx`;
  return { data: response.data, filename };
}
